import { Scan } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';

import selfiePlaceholder from '@/assets/images/selfie-placeholder.jpg';
import { cn } from '@/lib/utils';

/**
 * The live selfie capture — a 1:1 port of the designer's `SelfieCaptureScreen`
 * (react-new-html), values taken from `.sc-selfie-capture__*` rather than estimated.
 *
 * Shared by Face Match AND Liveness: both ask the applicant to put their face in the
 * same circular frame, so they get the same screen instead of two that drift apart.
 *
 * It opens the REAL camera. That is the point of the design — the applicant sees
 * themselves, not a mock — and it is also why the fallback matters: if permission is
 * denied, the device has no camera, or `getUserMedia` is unavailable (any non-HTTPS
 * origin), the placeholder portrait stays visible and the screen still reads
 * correctly instead of showing an empty black circle.
 */
/**
 * The ring's three states.
 *
 * `neutral` is the designer's gradient and the only thing the builder preview ever shows —
 * there is nobody in front of it to judge. A live run swaps it for green or red so the
 * applicant can see, without reading anything, whether they are about to be allowed to
 * capture.
 */
export type SelfieRingState = 'neutral' | 'ok' | 'bad';

const RING_BACKGROUND: Record<SelfieRingState, string> = {
  // The designer's `.sc-selfie-capture__ring` gradient, unchanged.
  neutral: 'linear-gradient(135deg, #4ABCFE 0%, #69FEDC 52%, #FDFEFF 100%)',
  // Flat brand green / red rather than a gradient: at 5px wide a gradient reads as a
  // colour somewhere between the two, which is exactly the distinction being made here.
  ok: '#00C232',
  bad: '#BF3636',
};

export function SelfieCaptureScreen({
  title = 'Selfie Capture',
  selfie,
  ringState = 'neutral',
  hint,
}: {
  title?: string;
  /** Live verdict on the preview — colours the ring. Defaults to the designer's gradient. */
  ringState?: SelfieRingState;
  /**
   * Replaces "Place your face within the frame" with the specific thing that is wrong
   * ("Move a little closer"). A concrete instruction is worth far more than a red ring the
   * applicant has to guess at.
   */
  hint?: string | null;
  /**
   * The live camera / captured still, supplied by the SESSION runner.
   *
   * In a real run the runner owns the camera — it has to, because it is the thing
   * that grabs the frame and uploads it. When this is passed we render it inside the
   * ring and never call `getUserMedia` ourselves, so the two never fight over the
   * device. The builder preview passes nothing and gets the self-view below.
   */
  selfie?: ReactNode;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraReady, setCameraReady] = useState(false);

  useEffect(() => {
    // The caller supplied the video — opening a second stream would take the camera
    // away from the runner that actually needs it.
    if (selfie) return;

    let cancelled = false;
    // Captured now: by cleanup time the ref may already have been detached, and the
    // stream still has to be stopped or the camera light stays on.
    const videoEl = videoRef.current;

    const startCamera = async () => {
      if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) return;

      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: { facingMode: 'user', width: { ideal: 720 }, height: { ideal: 720 } },
        });

        // The component may have unmounted while the permission prompt was open —
        // release the camera rather than leaving it running against a dead view.
        if (cancelled) {
          for (const track of stream.getTracks()) track.stop();
          return;
        }

        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) {
          for (const track of stream.getTracks()) track.stop();
          return;
        }

        video.srcObject = stream;
        try {
          await video.play();
          if (!cancelled) setCameraReady(true);
        } catch {
          // Autoplay refused — keep the placeholder rather than a blank circle.
          if (!cancelled) setCameraReady(false);
        }
      } catch {
        // Permission denied or no camera. Same outcome: keep the placeholder.
        if (!cancelled) setCameraReady(false);
      }
    };

    void startCamera();

    return () => {
      cancelled = true;
      const stream = streamRef.current;
      streamRef.current = null;
      for (const track of stream?.getTracks() ?? []) track.stop();
      if (videoEl) videoEl.srcObject = null;
    };
  }, [selfie]);

  return (
    <div className="relative flex h-full w-full flex-col items-center overflow-hidden rounded-xl px-4 pb-6 pt-5">
      {/* Header: icon + label on one line, not the big title/subtitle block the other
          screens use — at capture time the face is the subject, not the heading. */}
      <div className="z-10 flex shrink-0 items-center justify-center gap-2">
        <Scan size={22} strokeWidth={2} className="text-black" />
        <span className="text-[length:var(--text-18)] font-medium text-black">{title}</span>
      </div>

      <div className="relative flex min-h-0 w-full flex-1 items-center justify-center py-5">
        {/* The gradient ring is the border: a 5px padded wrapper whose background
            shows through around the circular video. */}
        <div
          className="relative flex aspect-square h-auto w-[min(340px,100%)] shrink-0 items-center justify-center rounded-full p-[5px] transition-[background] duration-200"
          style={{ background: RING_BACKGROUND[ringState] }}
        >
          <div className="relative h-full w-full overflow-hidden rounded-full bg-[#1A1F27]">
            {selfie ? (
              // The runner's own camera / captured still, clipped to the same circle.
              <div className="absolute inset-0 h-full w-full [&_img]:h-full [&_img]:w-full [&_img]:object-cover [&_video]:h-full [&_video]:w-full [&_video]:object-cover">
                {selfie}
              </div>
            ) : (
              <>
                <video
                  ref={videoRef}
                  // `scaleX(-1)` mirrors the feed so it behaves like a mirror — an
                  // unmirrored selfie preview feels wrong to move against.
                  className={cn(
                    'absolute inset-0 h-full w-full -scale-x-100 object-cover transition-opacity',
                    cameraReady ? 'opacity-100' : 'opacity-0',
                  )}
                  playsInline
                  muted
                  autoPlay
                >
                  {/* Present only to satisfy the a11y rule; there is no audio track and
                  nothing to caption on a live self-view. */}
                  <track kind="captions" />
                </video>
                {!cameraReady && (
                  <img
                    src={selfiePlaceholder}
                    alt="Selfie preview"
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* `whitespace-nowrap` keeps this on ONE line. At the designer's --text-16 it
          wrapped once the phone gained its 40px side padding, and a two-line pill
          reads as a warning rather than a hint. --text-14 plus nowrap fits the
          narrowest phone we support with room to spare. */}
      {/* Tinted to match the ring when something is wrong, so the instruction and the
          colour are read as one thing rather than two competing signals. */}
      <div
        className={cn(
          'z-10 shrink-0 whitespace-nowrap rounded-full px-[18px] py-2.5 text-center text-[length:var(--text-14)] font-semibold leading-[1.3]',
          ringState === 'bad'
            ? 'bg-[#BF36360D] text-[#BF3636] ring-1 ring-[#BF3636]'
            : 'bg-[#F3F4F6] text-[#111827]',
        )}
        // The hint changes while the applicant moves; announce it politely rather than
        // interrupting, or a screen reader would talk over every small adjustment.
        aria-live="polite"
      >
        {hint || 'Place your face within the frame'}
      </div>
    </div>
  );
}
